/**
 * Group Name Validation Utilities
 *
 * Provides validation functions for FGA group names.
 * Used by components to validate user input before API calls.
 *
 * @packageDocumentation
 */

import { SCIM_GROUP_PREFIX } from '../models/group.model';

/**
 * Validation error messages for group names
 */
export const GROUP_NAME_ERRORS = {
  REQUIRED: 'Group name is required',
  MIN_LENGTH: 'Group name must be at least 3 characters',
  MAX_LENGTH: 'Group name must be 120 characters or less',
  INVALID_CHARS: 'Group name can only contain letters, numbers, hyphens, and underscores',
  MUST_START_WITH_LETTER: 'Group name must start with a letter',
  MUST_END_WITH_ALPHANUMERIC: 'Group name must end with a letter or number',
  RESERVED_PREFIX:
    'Group name cannot start with "grp_". This prefix is reserved for groups managed by your Organisation',
  DUPLICATE: 'A group with this name already exists',
} as const;

/**
 * Minimum allowed length for group names
 */
export const GROUP_NAME_MIN_LENGTH = 3;

/**
 * Maximum allowed length for group names
 */
export const GROUP_NAME_MAX_LENGTH = 120;

/**
* Regex pattern for allowed characters inside a group name
* Allows: Unicode letters, Unicode digits, spaces, hyphen (-), underscore (_)
*/
export const GROUP_NAME_PATTERN = /^[\p{L}\p{N} _-]+$/u;

/**
 * Validation result type
 */
export interface ValidationResult {
  valid: boolean;
  error: string | null;
}

/**
 * Validates a group name against all rules.
 *
 * Validation rules:
 * 1. Name is required (not empty after trim)
 * 2. Minimum 3 characters
 * 3. Maximum 120 characters
 * 4. Only alphanumeric, hyphens, and underscores allowed
 * 5. Must not start with the `grp_` prefix (any case), reserved for SCIM groups
 * 6. Must start with a letter
 * 7. Must end with a letter or number
 *
 * @param name - The group name to validate
 * @returns ValidationResult with valid flag and optional error message
 *
 * @example
 * ```typescript
 * validateGroupName('my-team');     // { valid: true, error: null }
 * validateGroupName('');            // { valid: false, error: 'Group name is required' }
 * validateGroupName('ab');          // { valid: false, error: 'Group name must be at least 3 characters' }
 * validateGroupName('1team');       // { valid: false, error: 'Group name must start with a letter' }
 * validateGroupName('team-');       // { valid: false, error: 'Group name must end with a letter or number' }
 * validateGroupName('my team');     // { valid: false, error: '...invalid characters...' }
 * ```
 */
export function validateGroupName(name: string): ValidationResult {
  const trimmed = name.trim();

  if (!trimmed) {
    return { valid: false, error: GROUP_NAME_ERRORS.REQUIRED };
  }

  if (trimmed.length < GROUP_NAME_MIN_LENGTH) {
    return { valid: false, error: GROUP_NAME_ERRORS.MIN_LENGTH };
  }

  if (trimmed.length > GROUP_NAME_MAX_LENGTH) {
    return { valid: false, error: GROUP_NAME_ERRORS.MAX_LENGTH };
  }

  if (!GROUP_NAME_PATTERN.test(trimmed)) {
    return { valid: false, error: GROUP_NAME_ERRORS.INVALID_CHARS };
  }

  if (trimmed.toLowerCase().startsWith(SCIM_GROUP_PREFIX)) {
    return { valid: false, error: GROUP_NAME_ERRORS.RESERVED_PREFIX };
  }

  // Must start with a letter (a-z, A-Z)
  if (!/^[a-zA-Z]/.test(trimmed)) {
    return { valid: false, error: GROUP_NAME_ERRORS.MUST_START_WITH_LETTER };
  }

  // Must end with a letter or number (a-z, A-Z, 0-9)
  if (!/[a-zA-Z0-9]$/.test(trimmed)) {
    return { valid: false, error: GROUP_NAME_ERRORS.MUST_END_WITH_ALPHANUMERIC };
  }

  return { valid: true, error: null };
}

/**
 * Checks if a group name already exists in a list (case-insensitive).
 *
 * @param name - The group name to check
 * @param existingNames - Array of existing group names
 * @returns true if duplicate exists
 *
 * @example
 * ```typescript
 * isDuplicateGroupName('Engineering', ['engineering', 'sales']); // true
 * isDuplicateGroupName('support', ['engineering', 'sales']);     // false
 * ```
 */
export function isDuplicateGroupName(name: string, existingNames: string[]): boolean {
  const normalized = name.trim().toLowerCase();
  return existingNames.some(existing => existing.toLowerCase() === normalized);
}
